import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { ExpenseLedgerExportService } from '../services/expense-ledger-export.service';
import {
  ExpenseLedgerCategory,
  ExpenseLedgerItem,
  ExpenseLedgerService,
  ExpensePaymentMethod,
  ExpensePaymentStatus,
} from '../services/expense-ledger.service';

const currentMonth = (() => {
  const today = new Date();
  return {
    from: new Date(today.getFullYear(), today.getMonth(), 1),
    to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
  };
})();

function toDateFilter(date: Date | null): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'app-expense-page',
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    LucideAngularModule,
    DatePickerModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TableModule,
    StatusBadge,
  ],
  templateUrl: './expense-page.component.html',
})
export class ExpensePageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly expenseLedgerService = inject(ExpenseLedgerService);
  private readonly expenseLedgerExportService = inject(ExpenseLedgerExportService);

  readonly items = signal<ExpenseLedgerItem[]>([]);
  readonly searchTerm = signal('');
  readonly dateFrom = signal(toDateFilter(currentMonth.from));
  readonly dateTo = signal(toDateFilter(currentMonth.to));
  readonly category = signal<'ALL' | ExpenseLedgerCategory>('ALL');
  readonly status = signal<'ALL' | ExpensePaymentStatus>('ALL');

  readonly pendingDateFrom = signal<Date | null>(new Date(currentMonth.from));
  readonly pendingDateTo = signal<Date | null>(new Date(currentMonth.to));
  readonly pendingCategory = signal<'ALL' | ExpenseLedgerCategory>('ALL');
  readonly pendingStatus = signal<'ALL' | ExpensePaymentStatus>('ALL');
  readonly selectedExpenseId = signal<string | null>(null);
  readonly editDocumentUrl = signal('');
  readonly editTransferDate = signal<Date | null>(null);
  readonly editTransferTime = signal<Date | null>(null);
  readonly editWithholdingTaxAmount = signal(0);
  readonly documentUrlError = signal('');
  readonly transferDateTimeError = signal('');
  readonly withholdingTaxError = signal('');

  readonly selectedExpense = computed(
    () => this.items().find((item) => item.id === this.selectedExpenseId()) ?? null,
  );

  readonly categoryOptions: Array<{
    label: string;
    value: 'ALL' | ExpenseLedgerCategory;
  }> = [
    { label: 'ทุกประเภทรายจ่าย', value: 'ALL' },
    { label: 'ค่าใช้จ่าย', value: 'EXPENSE' },
    { label: 'คอมมิชชั่น', value: 'COMMISSION' },
  ];

  readonly statusOptions: Array<{
    label: string;
    value: 'ALL' | ExpensePaymentStatus;
  }> = [
    { label: 'ทุกสถานะ', value: 'ALL' },
    { label: 'ชำระแล้ว', value: 'PAID' },
    { label: 'ยกเลิก', value: 'CANCELLED' },
  ];

  readonly filteredItems = computed(() => {
    const query = this.searchTerm().trim().toLocaleLowerCase('th');
    if (!query) return this.items();
    return this.items().filter((item) =>
      [
        item.transactionNo,
        item.bookingNo,
        item.customerName,
        item.tourCode,
        item.tourName,
        item.country,
        item.description,
        item.requestedByName,
        item.bankName,
        item.accountNumber,
      ]
        .join(' ')
        .toLocaleLowerCase('th')
        .includes(query),
    );
  });

  readonly expenseTotal = computed(() =>
    this.filteredItems().reduce(
      (total, item) => total + (item.status === 'PAID' ? item.amount : 0),
      0,
    ),
  );

  ngOnInit(): void {
    void this.loadItems();
  }

  async applyFilters(): Promise<void> {
    this.dateFrom.set(toDateFilter(this.pendingDateFrom()));
    this.dateTo.set(toDateFilter(this.pendingDateTo()));
    this.category.set(this.pendingCategory());
    this.status.set(this.pendingStatus());
    await this.loadItems();
  }

  async resetFilters(): Promise<void> {
    this.searchTerm.set('');
    this.pendingDateFrom.set(new Date(currentMonth.from));
    this.pendingDateTo.set(new Date(currentMonth.to));
    this.pendingCategory.set('ALL');
    this.pendingStatus.set('ALL');
    await this.applyFilters();
  }

  paymentMethodLabel(method: ExpensePaymentMethod): string {
    return {
      BANK_TRANSFER: 'โอนผ่านธนาคาร',
      CASH: 'เงินสด',
      CREDIT_CARD: 'บัตรเครดิต',
    }[method];
  }

  categoryLabel(category: ExpenseLedgerCategory): string {
    return this.categoryOptions.find((option) => option.value === category)?.label ?? category;
  }

  async exportExcel(): Promise<void> {
    const items = this.filteredItems();
    if (items.length === 0) {
      this.overlay.error('ไม่สามารถ Export ได้', 'ไม่พบรายการรายจ่ายตามตัวกรองปัจจุบัน');
      return;
    }

    this.overlay.showLoading('กำลังสร้างไฟล์ Excel...');
    try {
      await this.expenseLedgerExportService.export(items, {
        dateFrom: this.dateFrom(),
        dateTo: this.dateTo(),
        category: this.optionLabel(this.categoryOptions, this.category()),
        status: this.optionLabel(this.statusOptions, this.status()),
        searchTerm: this.searchTerm().trim(),
      });
    } catch {
      this.overlay.error('สร้างไฟล์ Excel ไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    } finally {
      this.overlay.hideLoading();
    }
  }

  openDocument(url: string | null): void {
    if (!url) return;
    window.open(this.expenseLedgerService.resolveFileUrl(url), '_blank', 'noopener,noreferrer');
  }

  receiptDocumentUrl(item: ExpenseLedgerItem): string | null {
    return item.category === 'EXPENSE' ? item.receiptPath : item.documentUrl;
  }

  openEditDocument(item: ExpenseLedgerItem): void {
    if (item.status !== 'PAID') return;
    const transferredAt = new Date(item.transferredAt ?? item.paymentDate);
    this.selectedExpenseId.set(item.id);
    this.editDocumentUrl.set(item.documentUrl ?? '');
    this.editTransferDate.set(transferredAt);
    this.editTransferTime.set(transferredAt);
    this.editWithholdingTaxAmount.set(item.withholdingTaxAmount);
    this.documentUrlError.set('');
    this.transferDateTimeError.set('');
    this.withholdingTaxError.set('');
  }

  closeEditDocument(): void {
    this.selectedExpenseId.set(null);
    this.editDocumentUrl.set('');
    this.editTransferDate.set(null);
    this.editTransferTime.set(null);
    this.editWithholdingTaxAmount.set(0);
    this.documentUrlError.set('');
    this.transferDateTimeError.set('');
    this.withholdingTaxError.set('');
  }

  clearDocumentUrlError(): void {
    this.documentUrlError.set('');
  }

  clearTransferDateTimeError(): void {
    this.transferDateTimeError.set('');
  }

  clearWithholdingTaxError(): void {
    this.withholdingTaxError.set('');
  }

  async saveDocumentLink(): Promise<void> {
    const documentUrl = this.editDocumentUrl().trim();
    const validationError = this.validateOptionalUrl(documentUrl);
    this.documentUrlError.set(validationError);
    const transferDate = this.editTransferDate();
    const transferTime = this.editTransferTime();
    const transferDateTimeError =
      !transferDate || !transferTime ? 'กรุณาระบุวันที่และเวลาโอนเงิน' : '';
    this.transferDateTimeError.set(transferDateTimeError);
    if (validationError || transferDateTimeError || !transferDate || !transferTime) return;

    const selectedId = this.selectedExpenseId();
    const selectedExpense = this.selectedExpense();
    if (!selectedId || !selectedExpense) return;
    const withholdingTaxAmount = Number(this.editWithholdingTaxAmount());
    const withholdingTaxError =
      !Number.isFinite(withholdingTaxAmount) || withholdingTaxAmount < 0
        ? 'ยอดหัก ณ ที่จ่ายต้องไม่น้อยกว่า 0 บาท'
        : withholdingTaxAmount >= selectedExpense.amount
          ? 'ยอดหัก ณ ที่จ่ายต้องน้อยกว่ายอดจ่าย'
          : '';
    this.withholdingTaxError.set(withholdingTaxError);
    if (withholdingTaxError) return;

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการแก้ไขข้อมูลโอนจ่าย',
      message: `ยืนยันแก้ไขสลิปและวันเวลาโอนจ่ายของ ${selectedExpense.transactionNo} หรือไม่`,
      confirmText: 'ยืนยันการแก้ไข',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังบันทึกข้อมูลโอนจ่าย...');
    try {
      await this.expenseLedgerService.updateSlip(selectedId, {
        receiptUrl: documentUrl,
        transferDate: this.toDateValue(transferDate),
        transferTime: this.toTimeValue(transferTime),
        withholdingTaxAmount,
      });
      const transferredAt = this.toTransferDateTime(transferDate, transferTime).toISOString();
      this.items.update((items) =>
        items.map((item) =>
          item.id === selectedId
            ? {
                ...item,
                documentUrl: documentUrl || null,
                transferredAt,
                withholdingTaxAmount,
              }
            : item,
        ),
      );
      this.closeEditDocument();
      this.overlay.success('บันทึกสำเร็จ', 'แก้ไขข้อมูลโอนจ่ายเรียบร้อยแล้ว');
    } catch (error: unknown) {
      this.overlay.error('บันทึกข้อมูลโอนจ่ายไม่สำเร็จ', this.errorMessage(error));
    } finally {
      this.overlay.hideLoading();
    }
  }

  private validateOptionalUrl(value: string): string {
    if (!value) return '';
    try {
      const parsedUrl = new URL(value);
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
        ? ''
        : 'ลิงก์สลิปต้องขึ้นต้นด้วย http:// หรือ https://';
    } catch {
      return 'รูปแบบลิงก์สลิปไม่ถูกต้อง';
    }
  }

  private toDateValue(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toTimeValue(value: Date): string {
    return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
  }

  private toTransferDateTime(date: Date, time: Date): Date {
    const value = new Date(date);
    value.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return value;
  }

  private async loadItems(): Promise<void> {
    this.overlay.showLoading('กำลังโหลดรายการรายจ่าย...');
    try {
      this.items.set(
        await this.expenseLedgerService.getItems({
          dateFrom: this.dateFrom(),
          dateTo: this.dateTo(),
          category: this.category(),
          status: this.status(),
        }),
      );
    } catch (error: unknown) {
      this.overlay.error('โหลดรายการรายจ่ายไม่สำเร็จ', this.errorMessage(error));
    } finally {
      this.overlay.hideLoading();
    }
  }

  private errorMessage(error: unknown): string {
    return error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
      ? error.error.message
      : 'กรุณาลองใหม่อีกครั้ง';
  }

  private optionLabel<T extends string>(
    options: Array<{ label: string; value: T }>,
    value: T,
  ): string {
    return options.find((option) => option.value === value)?.label ?? value;
  }
}
